import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

const ACTION_TYPES = [
  'SET_VALUE', 'ASSIGN', 'APPROVE', 'REJECT', 'ESCALATE', 'NOTIFY', 'BLOCK', 'FLAG', 'CALCULATE', 'ROUTE',
];
const BRANCHES = ['THEN', 'ELSE'];

export function M24ActionBuilderPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [ruleId, setRuleId] = useState('');
  const [ruleLabel, setRuleLabel] = useState('');
  const [branch, setBranch] = useState('THEN');
  const [actionType, setActionType] = useState('APPROVE');
  const [targetField, setTargetField] = useState('');
  const [actionValue, setActionValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setSubmitting(true);
    apiRequest('/v1/rule-actions', {
      method: 'POST',
      token,
      body: {
        ruleId,
        branch,
        actionType,
        targetField: targetField || undefined,
        actionValue: actionValue || undefined,
      },
    })
      .then(() => { setMessage(t('m24.actionBuilder.msgAdded', { rule: ruleLabel })); setTargetField(''); setActionValue(''); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setSubmitting(false));
  }

  return (
    <main aria-label="action-builder">
      <h1>{t('m24.actionBuilder.title')}</h1>
      <form aria-label="action-form" onSubmit={onSubmit}>
        <RulePicker label={t('m24.actionBuilder.ruleId')} value={ruleId} onPick={(id, label) => { setRuleId(id); setRuleLabel(label); }} />
        <label htmlFor="branch">{t('m24.actionBuilder.branch')}</label>
        <select id="branch" value={branch} onChange={(e) => setBranch(e.target.value)}>
          {BRANCHES.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <label htmlFor="actionType">{t('m24.actionBuilder.actionType')}</label>
        <select id="actionType" value={actionType} onChange={(e) => setActionType(e.target.value)}>
          {ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <label htmlFor="targetField">{t('m24.actionBuilder.targetField')}</label>
        <input id="targetField" value={targetField} onChange={(e) => setTargetField(e.target.value)} />
        <label htmlFor="actionValue">{t('m24.actionBuilder.actionValue')}</label>
        <input id="actionValue" value={actionValue} onChange={(e) => setActionValue(e.target.value)} />
        <button type="submit" disabled={submitting || !ruleId.trim() || !actionType.trim()}>{t('m24.actionBuilder.submit')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
